import moment from "moment";
import "moment-timezone";
import {DateFormats, FormValues, ReservationStatus} from "../types";
import {useEffect, useState} from "react";


export const isFilled = <T>(value: T | null | undefined): value is T => value !== null && value !== undefined;

// Parse a server timestamp (UTC ISO 8601 with Z, or a tz-naive string we
// interpret as UTC) into a tz-aware moment anchored in the shop's zone.
// CLAUDE.md: every user-facing time renders in shop tz, never the browser's.
// Falls back to `moment.tz.guess()` only when the shop has no time_zone set.
export const toShopMoment = (s: string, tz?: string | null): moment.Moment => {
    const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(s)
    const utcInput = hasTz ? s : s.replace(' ', 'T') + 'Z'
    return moment.tz(utcInput, tz || moment.tz.guess())
}

export const transformFormValues = (
    values: FormValues,
    dateKeys: string[] = [],
    nullifyAll: boolean = true,
    nullifyKeys: string[] = [],
    dateFormat: DateFormats = DateFormats.Date
): any => {
    return Object.entries(values).reduce((acc, [key, value]) => {
        acc[key] = value;

        if ((nullifyKeys.includes(key) || nullifyAll) && value === '') {
            acc[key] = null;
        }

        if (dateKeys.includes(key) && value) {
            acc[key] = moment(value).utc().format(dateFormat);
        }

        return acc;
    }, {} as FormValues);
}

export const getReservationStatusColour = (status: ReservationStatus) => {
    switch (status) {
        case (ReservationStatus.NotConfirmed):
            return 'orange';
        case (ReservationStatus.Confirmed):
            return 'green';
        case (ReservationStatus.Cancelled):
            return 'gray';
        case (ReservationStatus.NoShow):
            return 'red';
        case (ReservationStatus.PendingClaim):
            // Violet matches the Priority Pass accent; the dashed border in
            // calendar CSS signals it's a soft hold, not yet confirmed.
            return 'priority.500';
        default:
            return 'gray';
    }
}

// CSS style for pending-claim events in the calendar — applied via inline style
// on calendar event wrappers. Matches WAITLIST.md §19.4's "distinct visual".
export const pendingClaimEventStyle = {
    backgroundColor: 'transparent',
    backgroundImage: 'repeating-linear-gradient(135deg, var(--chakra-colors-priority-500) 0 6px, transparent 6px 12px)',
    border: '2px dashed var(--chakra-colors-priority-500)',
    color: 'var(--chakra-colors-priority-700)',
}

export function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}
